/**
 * ArTable optimizer engine for Tables v2: reads the priced row set, sizes the
 * available markdown, calls the solver, and writes `distMarkdown` back through
 * the rows persist. Consumed by `ArTableOptimizerControls`.
 *
 * Formula invariant (SYD-2292): every mapped field that a formula targets is
 * computed here through the shared engine, never read from the persisted
 * `calcInput`. The paginated row source is `exportArTableRows`' raw `doc.data()`,
 * so trusting the cache dropped whole rows out of the totals — and this surface
 * persists what it computes, so a silently short total became a wrong markdown.
 *
 * Row scope (SYD-2429) is an argument, not a reading: every method takes an
 * `ArTableOptimizerRowScope`, so this hook never touches `uiState.selection` and
 * cannot disagree by a frame with the figures the popover shows. A ticked scope
 * is served from the loaded page even when the table is paginated, because every
 * selectable id is a rendered row or a member of a rendered header, hence an
 * already-loaded document.
 */

import { useCallback } from 'react';
import { useStoreNonReactive } from '@/store';
import type { OrderByOption } from '@/store/Store.types';
import { exportArTableRows } from '@/services/arTableFunctions';
import type { CollectionPersist, CollectionState, DocumentState, DocEntry } from '@/store';
import { runOptimizationCalculation } from '@/services/optimizationFunctions';
import type { CalculateOptimizationRequest, OptimizationResult } from '#types/System.Optimizer.types';
import { UIElementType } from '#types/System.types';
import { computeArRowFormulas, createArFormulaEngineConfig, readArStoredFormulaResult } from '#types';
import type { ArFormulaEngineConfig, ArRowFormulaComputation } from '#types';
import {
  AR_TABLE_AGGREGATIONS_ROW_ID,
  type ArTableId,
  type ArColumnDefinition,
  type ArRowData,
  type ArRowId,
  type ArTableOptimizerKey,
  type ArTableOptimizerMapping,
  type ArTableType,
  type TypeId,
} from '#types/System.Artifacts.types';
import { AR_TABLE_OPTIMIZER_BATCH_SIZE, AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import type { ArTableOptimizerRowScope } from './resolveOptimizerRowScope';

export type ArTableOptimizerReduction = {
  amount: number;
  percentage: number;
};

export type ArTableOptimizerTotals = {
  totalPrice: number;
  totalCommission: number;
  unlockedLineCount: number;
  totalLineCount: number;
};

export type ArTableOptimizerReadiness = {
  ready: boolean;
  reasons: string[];
  mapping: Record<ArTableOptimizerKey, TypeId>;
};

export type ArTableOptimizerRunResult = {
  result: OptimizationResult;
  updatedRowsCount: number;
  skippedRowsCount: number;
  failedRowsCount: number;
  optimizedRowCount: number;
  /**
   * Of `skippedRowsCount`, the markdown cells a Form Derive projection owns. Kept
   * apart so the report can name the reason instead of leaving the user to guess
   * which rows the run passed over (SYD-2404).
   */
  derivedCellsCount: number;
};

type UseArTableOptimizerParams = {
  tableId: ArTableId;
  parentPath: string;
  rowsOrderBy: OrderByOption;
  isPaginated: boolean;
};

type OptimizerRowSource = {
  rowIds: ArRowId[];
  rowsMap: Map<string, DocEntry<ArRowData>>;
};

type OptimizerSummary = {
  totals: ArTableOptimizerTotals;
  maxOBaseReduction: ArTableOptimizerReduction;
  maxCutPriceReduction: ArTableOptimizerReduction;
};

const TIMEOUT_MESSAGE = 'The optimizer timed out. Please let Syncretic know via the feedback button.';

const defaultMapping: Record<ArTableOptimizerKey, TypeId> = {
  extBidPrice: 'extBidPrice',
  oBasePrice: 'oBasePrice',
  oBaseComm: 'oBaseComm',
  overageComm: 'overageComm',
  cutPrice: 'cutPrice',
  cutComm: 'cutComm',
  quantity: 'quantity',
  distMarkdown: 'distMarkdown',
};

const requiredKeys: ReadonlyArray<ArTableOptimizerKey> = [
  'extBidPrice',
  'oBasePrice',
  'oBaseComm',
  'overageComm',
  'cutPrice',
  'cutComm',
  'quantity',
];

const isTimeoutError = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code;
  return code === 'deadline-exceeded' || code === 'functions/deadline-exceeded';
};

const resolveMappingTypeId = (
  mapping: ArTableOptimizerMapping | undefined,
  key: ArTableOptimizerKey
): TypeId => {
  const mapped = mapping?.[key];
  return typeof mapped === 'string' && mapped.length > 0 ? mapped : defaultMapping[key];
};

const readNumeric = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  if (value && typeof value === 'object') {
    const components = (value as { components?: Record<string, unknown> }).components;
    const price = components?.price;
    if (typeof price === 'number' && Number.isFinite(price)) {
      return price;
    }
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

type OptimizerValueReader = (params: {
  rowId: ArRowId;
  rowEntry: DocEntry<ArRowData>;
  typeId: TypeId;
}) => number | null;

/**
 * Reads one mapped field off a row as a number, computing formula columns at the
 * moment of use — one evaluation per row for the life of one reader, memoized by
 * rowId. A row the engine skips (aggregations, `suppressFormulas`, bundle header)
 * keeps its writer-set slot, which is what those rows exist to guarantee: a plain
 * target's `value` — its `calcInput` is an orphan no writer owns — and a dotted
 * target's composite sub-value, which stays writer-owned.
 */
const createOptimizerValueReader = (config: ArFormulaEngineConfig): OptimizerValueReader => {
  const formulaTypeIds = new Set<TypeId>(config.formulaSequence.map((entry) => entry.typeId));
  const computationByRowId = new Map<ArRowId, ArRowFormulaComputation>();

  return ({ rowId, rowEntry, typeId }) => {
    const cells = rowEntry.data?.cells;
    if (!formulaTypeIds.has(typeId)) return readNumeric(cells?.[typeId]?.value);

    const memoized = computationByRowId.get(rowId);
    const computation = memoized ?? computeArRowFormulas(rowId, rowEntry.data, config);
    if (!memoized) computationByRowId.set(rowId, computation);

    if (computation.skipped) {
      return readNumeric(
        typeId.includes('.') ? readArStoredFormulaResult(cells, typeId) : cells?.[typeId]?.value,
      );
    }
    return computation.results.get(typeId) ?? null;
  };
};

const calculateCommission = (params: {
  price: number;
  minRate: number;
  basePrice: number;
  baseRate: number;
  overRate: number;
}): number => {
  const { price, minRate, basePrice, baseRate, overRate } = params;
  if (price >= basePrice) {
    return basePrice * baseRate + (price - basePrice) * overRate;
  }
  return price * minRate;
};

const formatReductionAmount = (value: number): string => {
  return `$${value.toFixed(2)}`;
};

/**
 * A bundle header is a roll-up of its members, never a priceable line item: it
 * carries no own price and, since SYD-2276, no formula results either — the
 * engine skips it — so optimizing it would report every mapped field as missing
 * data. True of any header regardless of the table's bundle flags.
 */
const isOptimizableRow = (rowData: ArRowData | undefined): boolean => {
  return rowData?.bundleRole !== 'header';
};

const getUnlockedRows = (
  rowSource: OptimizerRowSource
): Array<{ rowId: ArRowId; rowNumber: number; rowEntry: DocEntry<ArRowData> }> => {
  return rowSource.rowIds
    .map((rowId, idx) => ({
      rowId,
      rowNumber: idx + 1,
      rowEntry: rowSource.rowsMap.get(rowId),
    }))
    .filter(
      (
        row
      ): row is { rowId: ArRowId; rowNumber: number; rowEntry: DocEntry<ArRowData> } =>
        row.rowEntry?.data !== undefined && row.rowEntry.data.locked !== true
    );
};

export function useArTableOptimizer({
  tableId,
  parentPath,
  rowsOrderBy,
  isPaginated,
}: UseArTableOptimizerParams) {
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw, persist: rowsPersistRaw } = useStoreNonReactive('rows');

  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;
  const persist = rowsPersistRaw as CollectionPersist<ArRowData>;

  if (persist.persistType !== 'collection') {
    throw new Error('[useArTableOptimizer] Expected collection persist for rows');
  }

  const getOptimizerEnabled = useCallback((): boolean => {
    return schemaState.data?.optimizer?.enabled === true;
  }, [schemaState]);

  const getEffectiveMapping = useCallback((): Record<ArTableOptimizerKey, TypeId> => {
    const mapping = schemaState.data?.optimizer?.mapping;
    return {
      extBidPrice: resolveMappingTypeId(mapping, 'extBidPrice'),
      oBasePrice: resolveMappingTypeId(mapping, 'oBasePrice'),
      oBaseComm: resolveMappingTypeId(mapping, 'oBaseComm'),
      overageComm: resolveMappingTypeId(mapping, 'overageComm'),
      cutPrice: resolveMappingTypeId(mapping, 'cutPrice'),
      cutComm: resolveMappingTypeId(mapping, 'cutComm'),
      quantity: resolveMappingTypeId(mapping, 'quantity'),
      distMarkdown: resolveMappingTypeId(mapping, 'distMarkdown'),
    };
  }, [schemaState]);

  const getReadiness = useCallback((): ArTableOptimizerReadiness => {
    const mapping = getEffectiveMapping();
    const formulaTypeIds = new Set<TypeId>(schemaState.data?.formulaSequence?.map((e) => e.typeId) ?? []);

    if (schemaState.data === null) {
      return { ready: false, reasons: ['Table schema is not loaded yet.'], mapping };
    }

    const columns = schemaState.data.columns ?? [];
    const columnIds = new Set(columns.map((c) => c.typeId));
    const columnByTypeId = new Map<TypeId, ArColumnDefinition>(columns.map((c) => [c.typeId, c]));

    const missing = requiredKeys
      .map((k) => ({ key: k, typeId: mapping[k] }))
      .filter(({ typeId }) => !columnIds.has(typeId));

    if (missing.length > 0) {
      return {
        ready: false,
        reasons: missing.map(({ key, typeId }) => `Missing column for ${key} (mapped to "${typeId}")`),
        mapping,
      };
    }

    const distMarkdownTypeId = mapping.distMarkdown;
    if (!columnIds.has(distMarkdownTypeId)) {
      return {
        ready: false,
        reasons: [`Missing column for distMarkdown (mapped to "${distMarkdownTypeId}")`],
        mapping,
      };
    }

    const distMarkdownColumn = columnByTypeId.get(distMarkdownTypeId);
    if (distMarkdownColumn?.uiElementType !== UIElementType.Field) {
      return {
        ready: false,
        reasons: ['distMarkdown output column must be a Field column.'],
        mapping,
      };
    }
    if (formulaTypeIds.has(distMarkdownTypeId)) {
      return {
        ready: false,
        reasons: ['distMarkdown output column cannot be a formula column.'],
        mapping,
      };
    }

    const protectedIds = new Set(schemaState.data.protected?.protectedTypeIds ?? []);
    if (protectedIds.has(distMarkdownTypeId)) {
      return {
        ready: false,
        reasons: ['distMarkdown output column is protected and cannot be updated by clients.'],
        mapping,
      };
    }

    return { ready: true, reasons: [], mapping };
  }, [getEffectiveMapping, schemaState]);

  /**
   * `rowIds` is the optimizer's row set — aggregations and bundle headers are
   * already out of it — in table order. `rowsMap` stays a plain lookup and may
   * still hold rows outside that set.
   *
   * A ticked set is served from the loaded page whether or not the table is
   * paginated: every selectable id is a rendered row or a member of a rendered
   * header, so it is a loaded document, and the export would only re-fetch what
   * is already here. Filtering `loadedRowIds` rather than iterating the ticks
   * keeps table order and drops an id the page does not hold. The whole-table
   * branches are unchanged.
   */
  const resolveRowSource = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<OptimizerRowSource> => {
    const loadedRowIds: ArRowId[] = rowsState.docIds.filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID);
    const isOptimizableLoadedRow = (rowId: ArRowId): boolean =>
      isOptimizableRow(rowsState.docs.get(rowId)?.data);

    if (rowScope.rowIds !== undefined) {
      const ticked = new Set<ArRowId>(rowScope.rowIds);
      return {
        rowIds: loadedRowIds.filter((rowId) => ticked.has(rowId) && isOptimizableLoadedRow(rowId)),
        rowsMap: rowsState.docs,
      };
    }

    const shouldExportAllRows = isPaginated || loadedRowIds.length >= AR_TABLE_PAGE_SIZE;

    if (!shouldExportAllRows) {
      return {
        rowIds: loadedRowIds.filter(isOptimizableLoadedRow),
        rowsMap: rowsState.docs,
      };
    }

    const response = await exportArTableRows({
      parentPath,
      tableId,
      orderBy: rowsOrderBy,
    });
    const rowsMap = new Map<string, DocEntry<ArRowData>>();
    const rowIds: ArRowId[] = [];

    for (const row of response.rows) {
      if (row.docId === AR_TABLE_AGGREGATIONS_ROW_ID) {
        continue;
      }

      rowsMap.set(row.docId, { data: row.data });
      if (isOptimizableRow(row.data)) {
        rowIds.push(row.docId as ArRowId);
      }
    }

    return {
      rowIds,
      rowsMap,
    };
  }, [isPaginated, parentPath, rowsOrderBy, rowsState.docIds, rowsState.docs, tableId]);

  const calculateTotalsForRowSource = useCallback((params: {
    rowSource: OptimizerRowSource;
    mapping: Record<ArTableOptimizerKey, TypeId>;
    readValue: OptimizerValueReader;
  }): ArTableOptimizerTotals => {
    const { rowSource, mapping, readValue } = params;
    const unlockedRows = getUnlockedRows(rowSource);

    const totals = unlockedRows.reduce((acc, { rowId, rowEntry }) => {
      if (!rowEntry.data) return acc;

      const extBidPrice = readValue({
        rowId,
        rowEntry,
        typeId: mapping.extBidPrice,
      }) ?? 0;

      const quantity = readValue({
        rowId,
        rowEntry,
        typeId: mapping.quantity,
      }) ?? 0;

      const oBasePrice = readValue({
        rowId,
        rowEntry,
        typeId: mapping.oBasePrice,
      }) ?? 0;

      const oBaseComm = (readValue({
        rowId,
        rowEntry,
        typeId: mapping.oBaseComm,
      }) ?? 0) / 100;

      const overageComm = (readValue({
        rowId,
        rowEntry,
        typeId: mapping.overageComm,
      }) ?? 0) / 100;

      const cutComm = (readValue({
        rowId,
        rowEntry,
        typeId: mapping.cutComm,
      }) ?? 0) / 100;

      const extBasePrice = oBasePrice * quantity;
      const commission = calculateCommission({
        price: extBidPrice,
        minRate: cutComm,
        basePrice: extBasePrice,
        baseRate: oBaseComm,
        overRate: overageComm,
      });

      return {
        totalPrice: acc.totalPrice + extBidPrice,
        totalCommission: acc.totalCommission + commission,
      };
    }, { totalPrice: 0, totalCommission: 0 });

    return {
      totalPrice: totals.totalPrice,
      totalCommission: totals.totalCommission,
      unlockedLineCount: unlockedRows.length,
      totalLineCount: rowSource.rowIds.length,
    };
  }, []);

  const calculateMaxReductionForRowSource = useCallback((params: {
    rowSource: OptimizerRowSource;
    mapping: Record<ArTableOptimizerKey, TypeId>;
    readValue: OptimizerValueReader;
    priceKey: 'cutPrice' | 'oBasePrice';
  }): ArTableOptimizerReduction => {
    const { rowSource, mapping, readValue, priceKey } = params;
    const unlockedRows = getUnlockedRows(rowSource);

    const totals = unlockedRows.reduce((acc, { rowId, rowEntry }) => {
      if (!rowEntry.data) return acc;

      const extBidPrice = readValue({
        rowId,
        rowEntry,
        typeId: mapping.extBidPrice,
      }) ?? 0;

      const comparePrice = readValue({
        rowId,
        rowEntry,
        typeId: mapping[priceKey],
      }) ?? 0;

      const quantity = readValue({
        rowId,
        rowEntry,
        typeId: mapping.quantity,
      }) ?? 0;

      const extComparePrice = comparePrice * quantity;

      return {
        totalDiff: acc.totalDiff + (extBidPrice - extComparePrice),
        totalExtBid: acc.totalExtBid + extBidPrice,
      };
    }, { totalDiff: 0, totalExtBid: 0 });

    const amount = Number(totals.totalDiff.toFixed(2));
    const percentage = totals.totalExtBid > 0
      ? Number(((totals.totalDiff / totals.totalExtBid) * 100).toFixed(2))
      : 0;

    return { amount, percentage };
  }, []);

  const calculateSummary = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<OptimizerSummary> => {
    const mapping = getEffectiveMapping();
    const schema = schemaState.data;
    if (!schema) {
      return {
        totals: { totalPrice: 0, totalCommission: 0, unlockedLineCount: 0, totalLineCount: 0 },
        maxOBaseReduction: { amount: 0, percentage: 0 },
        maxCutPriceReduction: { amount: 0, percentage: 0 },
      };
    }

    /** One reader across all three figures, so each row's formulas evaluate once. */
    const readValue = createOptimizerValueReader(createArFormulaEngineConfig(schema));
    const rowSource = await resolveRowSource(rowScope);

    return {
      totals: calculateTotalsForRowSource({ rowSource, mapping, readValue }),
      maxOBaseReduction: calculateMaxReductionForRowSource({
        rowSource,
        mapping,
        readValue,
        priceKey: 'oBasePrice',
      }),
      maxCutPriceReduction: calculateMaxReductionForRowSource({
        rowSource,
        mapping,
        readValue,
        priceKey: 'cutPrice',
      }),
    };
  }, [calculateMaxReductionForRowSource, calculateTotalsForRowSource, getEffectiveMapping, resolveRowSource, schemaState.data]);

  const calculateTotals = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<ArTableOptimizerTotals> => {
    const mapping = getEffectiveMapping();
    const schema = schemaState.data;
    if (!schema) return { totalPrice: 0, totalCommission: 0, unlockedLineCount: 0, totalLineCount: 0 };

    const readValue = createOptimizerValueReader(createArFormulaEngineConfig(schema));
    const rowSource = await resolveRowSource(rowScope);
    return calculateTotalsForRowSource({ rowSource, mapping, readValue });
  }, [calculateTotalsForRowSource, getEffectiveMapping, resolveRowSource, schemaState.data]);

  const calculateMaxReductionFor = useCallback(async (priceKey: 'cutPrice' | 'oBasePrice', rowScope: ArTableOptimizerRowScope): Promise<ArTableOptimizerReduction> => {
    const mapping = getEffectiveMapping();
    const schema = schemaState.data;
    if (!schema) return { amount: 0, percentage: 0 };

    const readValue = createOptimizerValueReader(createArFormulaEngineConfig(schema));
    const rowSource = await resolveRowSource(rowScope);
    return calculateMaxReductionForRowSource({ rowSource, mapping, readValue, priceKey });
  }, [calculateMaxReductionForRowSource, getEffectiveMapping, resolveRowSource, schemaState.data]);

  const calculateMaxReduction = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<ArTableOptimizerReduction> => {
    return calculateMaxReductionFor('cutPrice', rowScope);
  }, [calculateMaxReductionFor]);

  const calculateMaxObaseReduction = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<ArTableOptimizerReduction> => {
    return calculateMaxReductionFor('oBasePrice', rowScope);
  }, [calculateMaxReductionFor]);

  const updateMarkdownForRows = useCallback(async (params: {
    updates: Array<{ rowId: ArRowId; markdown: number }>;
    distMarkdownTypeId: TypeId;
    rowsMap: Map<string, DocEntry<ArRowData>>;
  }): Promise<{ updated: number; skipped: number; failed: number; derived: number }> => {
    const { updates, distMarkdownTypeId, rowsMap } = params;

    const batches = Array.from(
      { length: Math.ceil(updates.length / AR_TABLE_OPTIMIZER_BATCH_SIZE) },
      (_v, idx) => updates.slice(idx * AR_TABLE_OPTIMIZER_BATCH_SIZE, idx * AR_TABLE_OPTIMIZER_BATCH_SIZE + AR_TABLE_OPTIMIZER_BATCH_SIZE)
    );

    const updateOne = async (update: { rowId: ArRowId; markdown: number }): Promise<'updated' | 'skipped' | 'derived'> => {
      const rowEntry = rowsMap.get(update.rowId);
      if (!rowEntry?.data) return 'skipped';

      const isLocked = rowEntry.data.locked === true;
      const cellLocked = rowEntry.data.cells?.[distMarkdownTypeId]?.locked === true;
      if (isLocked || cellLocked) return 'skipped';
      /**
       * The Form Derive reconciler owns this markdown cell. Only the cell is
       * refused — the row's other columns stay the optimizer's to write, exactly
       * as they stay the person's to edit.
       */
      if (rowEntry.data.cells?.[distMarkdownTypeId]?._calculated === true) return 'derived';

      const loadedRowEntry = rowsState.docs.get(update.rowId);
      if (loadedRowEntry?.data) {
        if (!loadedRowEntry.data.cells) {
          loadedRowEntry.data.cells = {};
        }
        if (!loadedRowEntry.data.cells[distMarkdownTypeId]) {
          loadedRowEntry.data.cells[distMarkdownTypeId] = { value: null };
        }
        loadedRowEntry.data.cells[distMarkdownTypeId].value = update.markdown;
      }

      await persist.update(update.rowId, {
        [`cells.${distMarkdownTypeId}.value`]: update.markdown,
      }, { debounce: false });

      return 'updated';
    };

    type UpdateResult = 'updated' | 'skipped' | 'derived' | 'failed';

    const results = await batches.reduce<Promise<UpdateResult[]>>(
      async (accPromise, batch) => {
        const acc = await accPromise;
        const settled = await Promise.allSettled(batch.map(updateOne));
        const next = settled.map((r): UpdateResult => {
          if (r.status === 'fulfilled') return r.value;
          return 'failed';
        });
        return [...acc, ...next];
      },
      Promise.resolve([])
    );

    const updated = results.filter((r) => r === 'updated').length;
    const derived = results.filter((r) => r === 'derived').length;
    /** A derived cell was skipped too; `derived` names why, it does not exclude. */
    const skipped = results.filter((r) => r === 'skipped').length + derived;
    const failed = results.filter((r) => r === 'failed').length;
    return { updated, skipped, failed, derived };
  }, [persist, rowsState.docs]);

  const runOptimizer = useCallback(async (params: {
    targetReduction: number;
    rowScope: ArTableOptimizerRowScope;
  }): Promise<ArTableOptimizerRunResult> => {
    const { targetReduction, rowScope } = params;

    const readiness = getReadiness();
    if (!readiness.ready) {
      throw new Error(readiness.reasons.join('\n'));
    }

    const { mapping } = readiness;

    if (typeof targetReduction !== 'number' || targetReduction <= 0) {
      const formatted = typeof targetReduction === 'number' ? formatReductionAmount(targetReduction) : String(targetReduction);
      throw new Error(`Invalid reduction: ${formatted}\nAmount must be greater than $0.`);
    }

    const schema = schemaState.data;
    if (!schema) {
      throw new Error('Table schema is not loaded yet.');
    }

    const readValue = createOptimizerValueReader(createArFormulaEngineConfig(schema));
    const rowSource = await resolveRowSource(rowScope);

    if (rowSource.rowIds.length === 0) {
      throw new Error('Cannot optimize: No data provided.');
    }
    const unlockedRows = getUnlockedRows(rowSource);

    if (unlockedRows.length === 0) {
      throw new Error('Cannot optimize: All rows are locked.\nUnlock at least one row to proceed.');
    }

    const missingFieldsByRow = new Map<number, ArTableOptimizerKey[]>();

    const data = unlockedRows.map(({ rowId, rowNumber, rowEntry }, index): CalculateOptimizationRequest['data'][number] => {
      const readRequired = (key: ArTableOptimizerKey): number | null => {
        return readValue({
          rowId,
          rowEntry,
          typeId: mapping[key],
        });
      };

      const required = requiredKeys.reduce<Partial<Record<ArTableOptimizerKey, number>>>((acc, key) => {
        const n = readRequired(key);
        if (n === null) {
          const existing = missingFieldsByRow.get(rowNumber) ?? [];
          missingFieldsByRow.set(rowNumber, [...existing, key]);
          return acc;
        }
        acc[key] = n;
        return acc;
      }, {});

      const distMarkdown = readValue({
        rowId,
        rowEntry,
        typeId: mapping.distMarkdown,
      }) ?? 0;

      return {
        index,
        quantity: required.quantity ?? 0,
        extBidPrice: required.extBidPrice ?? 0,
        oBasePrice: required.oBasePrice ?? 0,
        oBaseComm: required.oBaseComm ?? 0,
        overageComm: required.overageComm ?? 0,
        cutPrice: required.cutPrice ?? 0,
        cutComm: required.cutComm ?? 0,
        distMarkdown,
      };
    });

    if (missingFieldsByRow.size > 0) {
      const header = 'Cannot optimize: Missing data.';
      const details = Array.from(missingFieldsByRow.entries())
        .map(([rowNum, fields]) => `Row ${rowNum}: ${fields.join(', ')}`)
        .join('\n');
      throw new Error(`${header}\n${details}`);
    }

    try {
      const response = await runOptimizationCalculation({
        data,
        targetReduction,
      });

      if (!response?.data) {
        throw new Error('Optimization failed: No response from server.\nPlease try again or contact support if the issue persists.');
      }

      const result = response.data as OptimizationResult;

      if (!result.modelMeta?.success) {
        throw new Error(result.modelMeta?.message || 'Optimization failed.\nPlease check your data and try again.');
      }

      if (!result.markdownPercents?.length) {
        throw new Error('Optimization failed: No results returned.\nThis may indicate a data processing error.');
      }

      if (result.markdownPercents.length !== unlockedRows.length) {
        throw new Error('Optimization failed: Unexpected result size.\nPlease try again or contact support.');
      }

      const updates = unlockedRows.map(({ rowId }, idx) => ({
        rowId,
        markdown: result.markdownPercents[idx] ?? 0,
      }));

      const { updated, skipped, failed, derived } = await updateMarkdownForRows({
        updates,
        distMarkdownTypeId: mapping.distMarkdown,
        rowsMap: rowSource.rowsMap,
      });

      return {
        result,
        updatedRowsCount: updated,
        skippedRowsCount: skipped,
        failedRowsCount: failed,
        optimizedRowCount: unlockedRows.length,
        derivedCellsCount: derived,
      };
    } catch (error) {
      if (isTimeoutError(error)) {
        throw new Error(TIMEOUT_MESSAGE);
      }
      throw error;
    }
  }, [getReadiness, resolveRowSource, schemaState.data, updateMarkdownForRows]);

  const clearMarkdown = useCallback(async (rowScope: ArTableOptimizerRowScope): Promise<{ cleared: number; skipped: number; failed: number; derived: number }> => {
    const readiness = getReadiness();
    if (!readiness.ready) {
      throw new Error(readiness.reasons.join('\n'));
    }

    const rowSource = await resolveRowSource(rowScope);
    const unlockedRows = getUnlockedRows(rowSource);
    const updates = unlockedRows.map(({ rowId }) => ({ rowId, markdown: 0 }));
    const { updated, skipped, failed, derived } = await updateMarkdownForRows({
      updates,
      distMarkdownTypeId: readiness.mapping.distMarkdown,
      rowsMap: rowSource.rowsMap,
    });
    return { cleared: updated, skipped, failed, derived };
  }, [getReadiness, resolveRowSource, updateMarkdownForRows]);

  return {
    getOptimizerEnabled,
    getReadiness,
    getEffectiveMapping,
    calculateSummary,
    calculateTotals,
    calculateMaxReduction,
    calculateMaxObaseReduction,
    runOptimizer,
    clearMarkdown,
  };
}

